import { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ArrowLeftRight, ArrowRight, ChevronLeft, ChevronRight, History, Loader2, Share2, Search, Trash2 } from "lucide-react";
import {
  fetchProposals as fetchProposalsApi,
  getProposalStatusHistory,
  type ProposalStatusHistoryEntry,
  updateProposalOwner,
  deleteProposal,
  deleteAllProposals,
} from "@/lib/proposalsApi";
import { listUsers, lookupUsersByRoles } from "@/lib/usersApi";
import { useAuth } from "@/contexts/AuthContext";
import DashboardLayout from "@/components/dashboard/DashboardLayout";
import { useToast } from "@/hooks/use-toast";
import {
  PROPOSAL_STATUS_FLOW,
  PROPOSAL_STATUS_LABELS,
  canShowShareButton,
  getStatusLabel,
} from "@/lib/proposalStatus";
import { ProposalStatusBadge } from "@/components/proposals/ProposalStatusBadge";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { getUserColorToken, getUserInitials } from "@/lib/userIdentity";

type Employee = {
  id: string;
  full_name?: string;
  role?: string | null;
  department?: string | null;
  profile_photo?: string | null;
};

type ProposalStatusTransition = {
  previousStatus: string | null;
  nextStatus: string | null;
  changedAt: string | null;
  description: string;
};

const Proposals = () => {
  const [proposals, setProposals] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [showShareDialog, setShowShareDialog] = useState(false);
  const [shareableLink, setShareableLink] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState<string>("all");
  const [ownerFilter, setOwnerFilter] = useState<string>("all");
  const [priorityFilter, setPriorityFilter] = useState<string>("all");
  const [employees, setEmployees] = useState<Employee[]>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalCount, setTotalCount] = useState(0);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [proposalToDelete, setProposalToDelete] = useState<any>(null);
  const [deleting, setDeleting] = useState(false);
  const [deleteAllDialogOpen, setDeleteAllDialogOpen] = useState(false);
  const [deletingAll, setDeletingAll] = useState(false);
  const [statusHistoryOpen, setStatusHistoryOpen] = useState(false);
  const [statusHistoryLoading, setStatusHistoryLoading] = useState(false);
  const [statusHistoryError, setStatusHistoryError] = useState<string | null>(null);
  const [statusHistoryRows, setStatusHistoryRows] = useState<ProposalStatusTransition[]>([]);
  const [statusHistoryTarget, setStatusHistoryTarget] = useState<any>(null);
  const { user, userRole } = useAuth();
  const navigate = useNavigate();
  const { toast } = useToast();

  const canCreate = userRole === "ceo" || userRole === "general_manager" || userRole === "sales_staff";
  const isCeo = userRole === "ceo";
  const isAdmin = userRole === "ceo" || userRole === "general_manager";
  const isSales = userRole === "sales_staff";
  const isFinance = userRole === "finance_staff";
  const canAccess = isSales || isFinance || isAdmin;
  // Sales can now view every proposal, so they get the owner filter/column too
  // (reassigning an owner stays admin-only via the Actions column).
  const showOwnerColumn = isAdmin || isSales;
  const columnCount = 9 + (showOwnerColumn ? 1 : 0) + (isAdmin ? 1 : 0);
  const statusOptions = useMemo(
    () =>
      PROPOSAL_STATUS_FLOW.filter((status) => {
        if (status === "canceled") {
          return isAdmin;
        }
        return true;
      }),
    [isAdmin]
  );

  const formatOwnerName = (name?: string) => {
    if (!name) return "";
    return name
      .trim()
      .split(/\s+/)
      .filter(Boolean)
      .map((part) => part.charAt(0).toUpperCase() + part.slice(1).toLowerCase())
      .join(" ");
  };

  const isSalesTeamMember = (employee: Employee) => {
    const department = (employee?.department || "").toString().toLowerCase();
    const role = (employee?.role || "").toString().toLowerCase();
    return department === "sales" || role === "sales_staff";
  };

  const salesEmployees = useMemo(
    () => (employees || []).filter((employee) => isSalesTeamMember(employee)),
    [employees]
  );

  const employeesById = useMemo(
    () => new Map((employees || []).map((employee) => [employee.id, employee])),
    [employees]
  );

  const totalPages = Math.ceil(totalCount / pageSize);

  const formatDateTime = (value?: string | null) => {
    if (!value) return "-";
    const parsed = new Date(value);
    if (Number.isNaN(parsed.getTime())) return "-";
    return parsed.toLocaleString();
  };

  const getStatusTransitionDescription = (previousStatus: string | null, nextStatus: string | null) => {
    const previousKey = previousStatus?.trim().toLowerCase() ?? "";
    const nextKey = nextStatus?.trim().toLowerCase() ?? "";

    if (!previousKey && nextKey === "to_do") return "Proposal created";
    if (previousKey === "to_do" && nextKey === "in_progress") return "Start building proposal";
    if (previousKey === "in_progress" && nextKey === "sent") return "Proposal sent to client";
    if (previousKey === "sent" && nextKey === "approved_to_be_paid") return "Proposal approved by client";
    if (previousKey === "approved_to_be_paid" && nextKey === "ready_to_book") return "Invoice uploaded";
    if (!previousStatus && nextStatus) return `Status set to ${getStatusLabel(nextStatus)}`;
    if (previousStatus && nextStatus) {
      return `Status changed from ${getStatusLabel(previousStatus)} to ${getStatusLabel(nextStatus)}`;
    }
    return "Status updated";
  };

  const buildStatusTransitions = (history: ProposalStatusHistoryEntry[]): ProposalStatusTransition[] => {
    return history
      .map((entry, index) => ({
        previousStatus: index > 0 ? history[index - 1]?.status ?? null : null,
        nextStatus: entry?.status ?? null,
        changedAt: entry?.entered_at ?? null,
        description: getStatusTransitionDescription(
          index > 0 ? history[index - 1]?.status ?? null : null,
          entry?.status ?? null,
        ),
      }))
      .filter((entry) => entry.nextStatus)
      .reverse();
  };

  const handlePageSizeChange = (value: string) => {
    setPageSize(parseInt(value, 10));
    setCurrentPage(1);
  };

  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery, statusFilter, ownerFilter, priorityFilter]);

  useEffect(() => {
    if (!showOwnerColumn) return;
    fetchEmployees();
  }, [showOwnerColumn]);

  useEffect(() => {
    if (isFinance && statusFilter !== "approved_to_be_paid") {
      setStatusFilter("approved_to_be_paid");
    }
  }, [isFinance, statusFilter]);

  useEffect(() => {
    if (!showOwnerColumn) return;
    if (ownerFilter === "all") return;
    const stillValid = salesEmployees.some((employee) => employee.id === ownerFilter);
    if (!stillValid) {
      setOwnerFilter("all");
    }
  }, [showOwnerColumn, ownerFilter, salesEmployees]);

  useEffect(() => {
    fetchProposals();
  }, [currentPage, pageSize, searchQuery, statusFilter, ownerFilter, priorityFilter]);

  const fetchEmployees = async () => {
    try {
      // Admins can list all users; sales staff can only use the authenticated
      // lookup endpoint, which returns proposal owners (sales + managers).
      const users = isAdmin
        ? await listUsers()
        : await lookupUsersByRoles(["ceo", "general_manager", "sales_staff"]);
      setEmployees(users || []);
    } catch {
      setEmployees([]);
    }
  };

  const fetchProposals = async () => {
    try {
      const response = await fetchProposalsApi({
        page: currentPage,
        size: pageSize,
        search: searchQuery.trim() || undefined,
        sortField: "created_at",
        sortOrder: "desc",
        status: isFinance ? "approved_to_be_paid" : statusFilter !== "all" ? statusFilter : undefined,
        priority: priorityFilter !== "all" ? priorityFilter : undefined,
        ownerId: ownerFilter !== "all" ? ownerFilter : undefined,
      });
      setProposals(response.items || []);
      setTotalCount(response.total || 0);
    } catch (error: any) {
      toast({
        title: "Error loading proposals",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const getPriorityBadge = (priority: string) => {
    const variants: Record<string, any> = {
      high: "destructive",
      medium: "secondary",
      low: "outline",
    };
    return <Badge variant={variants[priority] || "secondary"}>{priority?.toUpperCase() || "MEDIUM"}</Badge>;
  };

  const getClientTypeBadge = (type?: string) => {
    const key = (type || "final_client").toLowerCase();
    const variants: Record<string, any> = {
      agency: "default",
      final_client: "secondary",
      operator: "destructive",
      partner: "outline",
    };
    const labels: Record<string, string> = {
      agency: "Agency",
      final_client: "Final Client",
      operator: "Operator",
      partner: "Partner",
    };
    return <Badge variant={variants[key] || "secondary"}>{labels[key] || type || "Final Client"}</Badge>;
  };

  const getProposalClientProfile = (proposal: any): "agency" | "operator" | "final_client" | "partner" => {
    const partnerType = (proposal?.clients?.partner_business_type ?? proposal?.clients?.partnerBusinessType ?? "")
      .toString()
      .toLowerCase();
    const partnerId = proposal?.clients?.partner_id ?? proposal?.clients?.partnerId;
    const hasPartner = partnerId !== null && partnerId !== undefined;

    if (partnerType === "operator") return "operator";
    if (partnerType === "agency" || partnerType === "agent") return "agency";
    if (!hasPartner) return "final_client";
    return "partner";
  };

  const renderClientTypeCell = (proposal: any) => {
    const type = getProposalClientProfile(proposal);
    const operatorName =
      proposal?.clients?.operator_agency_name ??
      proposal?.clients?.operatorAgencyName ??
      proposal?.clients?.operator_agent_name;
    const showName = (type === "agency" || type === "operator") && operatorName;
    return (
      <div className="flex flex-col items-start gap-1">
        {getClientTypeBadge(type)}
        {showName && (
          <div className="text-xs text-muted-foreground leading-tight">
            {operatorName}
          </div>
        )}
      </div>
    );
  };

  const renderOwnerCell = (ownerId?: string | null) => {
    const employee = ownerId ? employeesById.get(ownerId) : undefined;
    const ownerName = formatOwnerName(employee?.full_name) || "Unassigned";
    const color = getUserColorToken(ownerId || ownerName);

    return (
      <div className="flex items-center gap-2">
        <Avatar className="h-8 w-8 border" style={{ borderColor: color.border }}>
          <AvatarImage src={employee?.profile_photo || undefined} alt={ownerName} />
          <AvatarFallback
            className="text-[10px] font-semibold"
            style={{ backgroundColor: color.bg, color: color.text }}
          >
            {getUserInitials(ownerName)}
          </AvatarFallback>
        </Avatar>
        <span className="text-sm font-medium">{ownerName}</span>
      </div>
    );
  };

  const handleOwnerChange = async (proposalId: string, newOwnerId: string) => {
    try {
      await updateProposalOwner(proposalId, newOwnerId);
    } catch (error: any) {
      toast({
        title: "Error updating owner",
        description: error?.message || "Failed to update owner.",
        variant: "destructive",
      });
      return;
    }

    toast({
      title: "Owner updated",
      description: "The proposal owner has been changed successfully.",
    });

    fetchProposals();
  };

  const handleShare = (proposalId: string) => {
    const link = `${window.location.origin}/proposal/${proposalId}`;
    setShareableLink(link);
    setShowShareDialog(true);
  };

  const copyLink = () => {
    navigator.clipboard.writeText(shareableLink);
    toast({
      title: "Link copied!",
      description: "The proposal link has been copied to clipboard.",
    });
  };

  const openProposal = () => {
    window.open(shareableLink, "_blank");
  };

  const loadStatusHistory = async (proposal: any) => {
    try {
      setStatusHistoryLoading(true);
      setStatusHistoryError(null);
      const history = await getProposalStatusHistory(proposal.id);
      setStatusHistoryRows(buildStatusTransitions(history));
    } catch (error: any) {
      const message = error?.message || "Failed to load status history.";
      setStatusHistoryError(message);
      toast({
        title: "Error loading status history",
        description: message,
        variant: "destructive",
      });
    } finally {
      setStatusHistoryLoading(false);
    }
  };

  const handleOpenStatusHistory = (proposal: any) => {
    setStatusHistoryTarget(proposal);
    setStatusHistoryRows([]);
    setStatusHistoryOpen(true);
    void loadStatusHistory(proposal);
  };

  const handleDeleteClick = (proposal: any) => {
    setProposalToDelete(proposal);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!proposalToDelete) return;

    try {
      setDeleting(true);
      await deleteProposal(proposalToDelete.id);

      toast({
        title: "Proposal deleted",
        description: `Proposal "${proposalToDelete.title}" has been deleted successfully.`,
      });

      setDeleteDialogOpen(false);
      setProposalToDelete(null);
      fetchProposals();
    } catch (error: any) {
      toast({
        title: "Error deleting proposal",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setDeleting(false);
    }
  };

  const handleDeleteAllConfirm = async () => {
    try {
      setDeletingAll(true);
      if (totalCount === 0) {
        toast({ title: "No proposals to delete", description: "The list is already empty." });
        setDeleteAllDialogOpen(false);
        return;
      }
      await deleteAllProposals();
      toast({
        title: "All proposals deleted",
        description: "All proposal data has been removed from this screen.",
      });
      setDeleteAllDialogOpen(false);
      fetchProposals();
    } catch (error: any) {
      toast({
        title: "Error deleting proposals",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setDeletingAll(false);
    }
  };

  if (!canAccess) {
    return (
      <DashboardLayout>
        <div className="text-center py-12">
          <h2 className="text-xl font-semibold">Access Denied</h2>
          <p className="text-muted-foreground">
            Only Sales, Finance, Managers, and CEO can access Proposals.
          </p>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold">Proposals</h1>
            <p className="text-muted-foreground">
              {isFinance ? "Approved proposals waiting for payment handling" : "Create and manage travel proposals for clients"}
            </p>
          </div>
          <div className="flex gap-2" />
        </div>

        <div className="flex items-center gap-4 mb-4 flex-wrap">
          <div className="relative flex-1 max-w-sm min-w-[200px]">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search proposals..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10"
            />
          </div>
          {isFinance ? (
            <Badge variant="secondary" className="h-10 px-3 text-sm">
              Status: {PROPOSAL_STATUS_LABELS.approved_to_be_paid}
            </Badge>
          ) : (
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-[220px]">
                <SelectValue placeholder="Filter by status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Statuses</SelectItem>
                {statusOptions.map((status) => (
                  <SelectItem key={status} value={status}>
                    {PROPOSAL_STATUS_LABELS[status]}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
          {showOwnerColumn && (
            <Select value={ownerFilter} onValueChange={setOwnerFilter}>
              <SelectTrigger className="w-[180px]">
                <SelectValue placeholder="Filter by owner" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Owners</SelectItem>
                {salesEmployees.map((employee) => (
                  <SelectItem key={employee.id} value={employee.id}>
                    {formatOwnerName(employee.full_name)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
          <Select value={priorityFilter} onValueChange={setPriorityFilter}>
            <SelectTrigger className="w-[180px]">
              <SelectValue placeholder="Filter by priority" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Priorities</SelectItem>
              <SelectItem value="high">High</SelectItem>
              <SelectItem value="medium">Medium</SelectItem>
              <SelectItem value="low">Low</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <div className="overflow-x-auto rounded-md border">
          <Table className="min-w-[1200px]">
            <TableHeader>
              <TableRow>
                <TableHead>Internal Reference</TableHead>
                <TableHead>Title</TableHead>
                <TableHead>Client</TableHead>
                <TableHead>Type</TableHead>
                {showOwnerColumn && <TableHead>Owner</TableHead>}
                <TableHead>Priority</TableHead>
                <TableHead>Destination</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Total Value</TableHead>
                <TableHead>Created</TableHead>
                {isAdmin && <TableHead className="text-right">Actions</TableHead>}
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={columnCount} className="text-center">
                    Loading...
                  </TableCell>
                </TableRow>
              ) : totalCount === 0 ? (
                <TableRow>
                  <TableCell colSpan={columnCount} className="text-center">
                    {searchQuery ? "No proposals match your search" : "No proposals found"}
                  </TableCell>
                </TableRow>
              ) : (
                proposals.map((proposal) => (
                  <TableRow 
                    key={proposal.id}
                    className="cursor-pointer hover:bg-muted/50"
                    onClick={() => navigate(`/proposals/${proposal.id}`)}
                  >
                    <TableCell className="font-medium">
                      {proposal.internal_reference || "-"}
                    </TableCell>
                    <TableCell>{proposal.title}</TableCell>
                    <TableCell>
                      {proposal.clients?.company ||
                        `${proposal.clients?.first_name} ${proposal.clients?.last_name}`}
                    </TableCell>
                    <TableCell>{renderClientTypeCell(proposal)}</TableCell>
                    {showOwnerColumn && (
                      <TableCell>
                        {renderOwnerCell(proposal.owner_id)}
                      </TableCell>
                    )}
                    <TableCell>{getPriorityBadge(proposal.priority)}</TableCell>
                    <TableCell>{proposal.main_destination}</TableCell>
                    <TableCell><ProposalStatusBadge status={proposal.status} size="sm" /></TableCell>
                    <TableCell className="font-medium">
                      ${proposal.total_value?.toFixed(2) || "0.00"}
                    </TableCell>
                    <TableCell>
                      {new Date(proposal.created_at).toLocaleDateString()}
                    </TableCell>
                    {isAdmin && (
                      <TableCell className="text-right" onClick={(e) => e.stopPropagation()}>
                        <div className="flex justify-end gap-2 items-center">
                          {isCeo && (
                            <Button
                              variant="ghost"
                              size="sm"
                              title="View history"
                              onClick={(e) => {
                                e.stopPropagation();
                                navigate(`/proposals/${proposal.id}/history`);
                              }}
                            >
                              <History className="h-4 w-4" />
                            </Button>
                          )}
                          <Button
                            variant="ghost"
                            size="sm"
                            title="View status history"
                            aria-label="View status history"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleOpenStatusHistory(proposal);
                            }}
                          >
                            <ArrowLeftRight className="h-4 w-4" />
                          </Button>
                          {canShowShareButton(proposal.status) && (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleShare(proposal.id);
                              }}
                            >
                              <Share2 className="h-4 w-4" />
                            </Button>
                          )}
                          {isCeo && (
                            <Button
                              variant="ghost"
                              size="sm"
                              className="text-destructive hover:text-destructive hover:bg-destructive/10"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleDeleteClick(proposal);
                              }}
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          )}
                          <Select
                            value={proposal.owner_id || ""}
                            onValueChange={(value) => handleOwnerChange(proposal.id, value)}
                          >
                            <SelectTrigger className="w-[180px]">
                              <SelectValue placeholder="Change owner" />
                            </SelectTrigger>
                            <SelectContent>
                              {employees.map((employee) => (
                                <SelectItem key={employee.id} value={employee.id}>
                                  {formatOwnerName(employee.full_name)}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                      </TableCell>
                    )}
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>

        {totalCount > 0 && (
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Select value={pageSize.toString()} onValueChange={handlePageSizeChange}>
                <SelectTrigger className="w-[70px]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="10">10</SelectItem>
                  <SelectItem value="50">50</SelectItem>
                  <SelectItem value="100">100</SelectItem>
                </SelectContent>
              </Select>
              <div className="text-sm text-muted-foreground">
                Showing {totalCount > 0 ? (currentPage - 1) * pageSize + 1 : 0} to{" "}
                {Math.min(currentPage * pageSize, totalCount)} of {totalCount} records
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setCurrentPage(Math.max(currentPage - 1, 1))}
                disabled={currentPage === 1 || loading}
              >
                <ChevronLeft className="h-4 w-4" />
              </Button>
              <div className="text-sm">
                Page {currentPage} of {totalPages || 1}
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setCurrentPage(Math.min(currentPage + 1, Math.max(totalPages, 1)))}
                disabled={currentPage >= totalPages || loading}
              >
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
          </div>
        )}

        <Dialog open={showShareDialog} onOpenChange={setShowShareDialog}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Shareable Proposal</DialogTitle>
              <DialogDescription>
                The proposal has been successfully finalized! Use the link below to share with the client.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4">
              <div className="p-3 bg-muted rounded-md break-all text-sm">
                {shareableLink}
              </div>
              <div className="flex gap-2">
                <Button onClick={copyLink} className="flex-1">
                  Copy Link
                </Button>
                <Button onClick={openProposal} variant="outline" className="flex-1">
                  Open Proposal
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        <Dialog
          open={statusHistoryOpen}
          onOpenChange={(open) => {
            setStatusHistoryOpen(open);
            if (!open) {
              setStatusHistoryError(null);
              setStatusHistoryRows([]);
              setStatusHistoryTarget(null);
            }
          }}
        >
          <DialogContent className="max-w-4xl">
            <DialogHeader>
              <DialogTitle>Status History</DialogTitle>
              <DialogDescription>
                {statusHistoryTarget
                  ? `Status changes for proposal ${statusHistoryTarget.internal_reference || statusHistoryTarget.title || statusHistoryTarget.id}.`
                  : "Status changes for the selected proposal."}
              </DialogDescription>
            </DialogHeader>

            <div className="max-h-[60vh] overflow-y-auto rounded-md border">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Date and Time</TableHead>
                    <TableHead>Description</TableHead>
                    <TableHead>Previous Status</TableHead>
                    <TableHead>New Status</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {statusHistoryLoading ? (
                    <TableRow>
                      <TableCell colSpan={4} className="py-10 text-center">
                        <div className="flex items-center justify-center gap-2 text-muted-foreground">
                          <Loader2 className="h-4 w-4 animate-spin" />
                          <span>Loading status history...</span>
                        </div>
                      </TableCell>
                    </TableRow>
                  ) : statusHistoryError ? (
                    <TableRow>
                      <TableCell colSpan={4} className="py-10 text-center">
                        <div className="space-y-3">
                          <p className="text-sm text-destructive">{statusHistoryError}</p>
                          {statusHistoryTarget && (
                            <Button variant="outline" size="sm" onClick={() => void loadStatusHistory(statusHistoryTarget)}>
                              Try Again
                            </Button>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  ) : statusHistoryRows.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={4} className="py-10 text-center text-muted-foreground">
                        No status history found for this proposal.
                      </TableCell>
                    </TableRow>
                  ) : (
                    statusHistoryRows.map((entry, index) => (
                      <TableRow key={`${entry.changedAt || "unknown"}-${entry.nextStatus || "unknown"}-${index}`}>
                        <TableCell className="whitespace-nowrap font-medium">
                          {formatDateTime(entry.changedAt)}
                        </TableCell>
                        <TableCell className="min-w-[260px] text-sm text-foreground/80">
                          {entry.description}
                        </TableCell>
                        <TableCell>
                          {entry.previousStatus ? (
                            <ProposalStatusBadge status={entry.previousStatus} size="sm" />
                          ) : (
                            <span className="text-sm text-muted-foreground">-</span>
                          )}
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            {entry.previousStatus && <ArrowRight className="h-4 w-4 text-muted-foreground" />}
                            <ProposalStatusBadge status={entry.nextStatus} size="sm" />
                          </div>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </div>
          </DialogContent>
        </Dialog>

        <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Delete Proposal</AlertDialogTitle>
              <AlertDialogDescription>
                Are you sure you want to delete the proposal "{proposalToDelete?.title}"? 
                This action cannot be undone and will permanently remove all associated data 
                (accommodations, tours, transfers, flights, etc.).
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel disabled={deleting}>Cancel</AlertDialogCancel>
              <AlertDialogAction
                onClick={handleDeleteConfirm}
                disabled={deleting}
                className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              >
                {deleting ? "Deleting..." : "Delete Proposal"}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        <AlertDialog open={deleteAllDialogOpen} onOpenChange={setDeleteAllDialogOpen}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Delete all proposals</AlertDialogTitle>
              <AlertDialogDescription>
                This will permanently delete all proposals and their related data from this screen. This action cannot be undone.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel disabled={deletingAll}>Cancel</AlertDialogCancel>
              <AlertDialogAction
                onClick={handleDeleteAllConfirm}
                disabled={deletingAll}
                className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              >
                {deletingAll ? "Deleting..." : "Delete all"}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </DashboardLayout>
  );
};

export default Proposals;
